import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { NotificationInfo, PreviewHeader } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { BackToTopButton } from 'src/components/molecules/BackToTopButton/BackToTopButton';
import { TaxonomyBreadcrumbsContainer } from 'src/components/molecules/Breadcrumbs/TaxonomyBreadcrumbsContainer';
import { CopyBlockContainer } from 'src/components/molecules/CopyBlock/CopyBlockContainer';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { ProductListError } from 'src/components/molecules/ProductListError/ProductListError';
import { TaxonomyNavContainer } from 'src/components/molecules/TaxonomyNav/TaxonomyNavContainer';
import type { LinkCarouselContainerProps } from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import {
  LinkCarouselContainer,
  LinkCarouselTrackingCategory,
} from 'src/components/organisms/LinkCarousel/LinkCarouselContainer';
import { ListingsControlsDrawerContainer } from 'src/components/organisms/ListingsControlsDrawer/ListingsControlsDrawerContainer';
import { RELATED_CATEGORIES_TITLE } from 'src/components/organisms/ProductPages/constants/copy';
import { TaxonomyHeaderBlockContainer } from 'src/components/organisms/TaxonomyHeaderBlock/TaxonomyHeaderBlockContainer';
import { useFilterNavType } from 'src/hooks/useFilterNavType/useFilterNavType';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { ProductsState } from 'src/redux/products/types';
import * as styles from 'src/styles/pages.styles';
import type { NextConfig } from 'src/types/nextConfig';
import type { PageProps } from 'src/types/page';
import { ListingsLayout } from '../ListingLayout/ListingsLayout';

const {
  publicRuntimeConfig: { CLEAR_PREVIEW_API_URL },
} = getConfig() as NextConfig;

/** Props supplied by the page (GSSP); `TaxonomyListingContainer` merges Redux state fields. */
export type TaxonomyListingOwnProps = Pick<
  PageProps,
  'featureFlags' | 'pageEventData' | 'siteChromeContent' | 'updatedReduxState'
> & {
  bottomLinks: LinkCarouselContainerProps['links'];
  preview: boolean;
  stickyTopBanner?: boolean;
  topLinks: LinkCarouselContainerProps['links'];
};

export type TaxonomyListingProps = TaxonomyListingOwnProps & {
  pageLiveRegionText: string;
  products: ProductsState;
  showProductsList: boolean;
  stickyTopBanner: boolean;
};

export const TaxonomyListing = ({
  bottomLinks,
  pageLiveRegionText,
  preview,
  showProductsList,
  stickyTopBanner = false,
  topLinks,
  updatedReduxState,
}: TaxonomyListingProps) => {
  const { query } = useRouter();
  // useDispatch rather than useStore(): server-side getStore() always builds a
  // fresh store, so calling it here constructed an entire throwaway store per
  // SSR render just to reach dispatch for the client-only effect below.
  const dispatch = useDispatch();
  const { products } = updatedReduxState as ReduxApplicationState;
  const [redirectNotification, setRedirectNotification] = useState(false);
  const relatedLinks = [...topLinks, ...bottomLinks];

  useFilterNavType((filterNavType) =>
    dispatch(navigationActions.setInitialPageLoad(filterNavType)),
  );

  useEffect(() => {
    if (query.redirectedFrom === 'pdp') {
      setRedirectNotification(true);
    }
  }, [query.redirectedFrom]);

  return (
    <>
      {preview && <PreviewHeader href={CLEAR_PREVIEW_API_URL} />}
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.breadcrumbsContentWrapper}>
            <TaxonomyBreadcrumbsContainer />
          </div>
        </div>
        <div css={[styles.grid, maxWidthContainer]}>
          <div css={styles.headerContentWrapper}>
            {redirectNotification && (
              <NotificationInfo>
                We don&apos;t seem to have that product anymore, but here are some similar items
                below.
              </NotificationInfo>
            )}
            <TaxonomyHeaderBlockContainer />
            <TaxonomyNavContainer />
          </div>
        </div>

        {showProductsList ? (
          <ListingsLayout stickyTopBanner={stickyTopBanner}>
            {relatedLinks.length > 0 && (
              <div css={styles.relatedCategories}>
                <LinkCarouselContainer
                  links={relatedLinks}
                  title={RELATED_CATEGORIES_TITLE}
                  trackingCategory={LinkCarouselTrackingCategory.RelatedCategory}
                />
              </div>
            )}
            <CopyBlockContainer />
            <MembershipBannerSection trackingLocation="shop" />
            <BackToTopButton />
            <ListingsControlsDrawerContainer />
          </ListingsLayout>
        ) : (
          <ProductListError
            message={products.error ? products.error.message : 'No products found'}
          />
        )}
      </div>
    </>
  );
};
